import { useState, useCallback } from "react";

export type CallMediaType = "audio" | "video";

type DeviceCheckState = {
  hasCamera: boolean;
  hasMicrophone: boolean;
  cameraError: string | null;
  microphoneError: string | null;
};

type DeviceCheckResult = DeviceCheckState & {
  isChecking: boolean;
  checkDevices: (callType?: CallMediaType) => Promise<DeviceCheckState>;
};

export const useDeviceCheck = (): DeviceCheckResult => {
  const [state, setState] = useState<DeviceCheckState>({
    hasCamera: false,
    hasMicrophone: false,
    cameraError: null,
    microphoneError: null,
  });
  const [isChecking, setIsChecking] = useState(false);

  const checkDevices = useCallback(async (callType: CallMediaType = "video") => {
    setIsChecking(true);
    const needsCamera = callType === "video";

    let result: DeviceCheckState = {
      hasCamera: false,
      hasMicrophone: false,
      cameraError: null,
      microphoneError: null,
    };

    try {
      // Permission probe: audio-only for voice calls, audio+video for video calls.
      // This is what the browser permission prompt is based on.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: needsCamera,
      });
      stream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === "videoinput");
      const audioDevices = devices.filter((d) => d.kind === "audioinput");

      result.hasCamera = videoDevices.length > 0;
      result.hasMicrophone = audioDevices.length > 0;

      if (needsCamera && !result.hasCamera) {
        result.cameraError = "No camera device found. Please connect a camera.";
      }

      if (!result.hasMicrophone) {
        result.microphoneError =
          "No microphone found. Please connect a microphone.";
      }
    } catch (err: any) {
      const name = String(err?.name || "");
      const message = String(err?.message || "");

      if (!needsCamera) {
        result.microphoneError =
          name === "NotAllowedError" || name === "PermissionDeniedError"
            ? "Microphone permission denied. Allow microphone access to make audio calls."
            : "Unable to access microphone. Please check your device settings.";
      } else if (
        name === "NotFoundError" ||
        /video/i.test(message) ||
        /camera/i.test(message)
      ) {
        // Could be camera-only failure; still try to clarify.
        result.cameraError =
          "Unable to access camera. Please allow camera access for video calls.";
        result.microphoneError =
          "Unable to access microphone. Please allow microphone access for video calls.";
      } else if (
        name === "NotAllowedError" ||
        name === "PermissionDeniedError"
      ) {
        result.cameraError =
          "Camera/microphone permission denied. Allow access to make video calls.";
        result.microphoneError =
          "Camera/microphone permission denied. Allow access to make video calls.";
      } else {
        result.cameraError = "An error occurred while checking camera.";
        result.microphoneError =
          "An error occurred while checking microphone.";
      }
    } finally {
      setState(result);
      setIsChecking(false);
    }

    return result;
  }, []);

  return {
    ...state,
    isChecking,
    checkDevices,
  };
};
